"use client"

import { useState, useMemo } from "react"
import { usePlayback } from "./usePlayback"
import { PlaybackControls } from "./PlaybackControls"
import type { LinkedListNode, LinkedListStep } from "./types"

// ── Props ──

interface LinkedListVizProps {
  initialList: LinkedListNode[]
  steps: LinkedListStep[]
  width?: number
  height?: number
  doubly?: boolean
}

// ── Internal node carrying visual state ──

interface InternalNode {
  id: number
  value: number | string
  highlightColor?: string
  isNew?: boolean
  isDeleted?: boolean
  pointerHighlighted?: boolean
}

// ── Layout constants ──

const NODE_W = 60
const NODE_H = 40
const GAP = 30
const NODE_RX = 8
const CELL_W = NODE_W + GAP // total horizontal space per node

// ── Build the list state at a given step index ──

function buildListAtStep(
  initialList: LinkedListNode[],
  steps: LinkedListStep[],
  targetIndex: number
): InternalNode[] {
  // Start from a clean copy of the initial list
  let nodes: InternalNode[] = initialList.map((n) => ({
    id: n.id,
    value: n.value,
  }))

  for (let i = 0; i <= targetIndex; i++) {
    const step = steps[i]!

    switch (step.type) {
      case "traverse": {
        // Clear previous traversal highlights, then highlight target
        nodes = nodes.map((n) => ({
          ...n,
          highlightColor:
            n.id === step.nodeId ? "var(--color-primary)" : n.highlightColor,
          pointerHighlighted: false,
        }))
        break
      }

      case "insert": {
        if (step.value == null) break
        const newNode: InternalNode = {
          id: step.nodeId ?? Date.now(),
          value: step.value,
          isNew: true,
        }
        if (step.afterId == null) {
          // Insert at head
          nodes = [newNode, ...nodes]
        } else {
          const idx = nodes.findIndex((n) => n.id === step.afterId)
          if (idx !== -1) {
            nodes = [...nodes.slice(0, idx + 1), newNode, ...nodes.slice(idx + 1)]
          } else {
            nodes = [...nodes, newNode]
          }
        }
        break
      }

      case "delete": {
        if (step.nodeId == null) break
        nodes = nodes.filter((n) => n.id !== step.nodeId)
        break
      }

      case "repoint": {
        nodes = nodes.map((n) => ({
          ...n,
          pointerHighlighted: n.id === step.nodeId,
        }))
        break
      }

      case "highlight": {
        if (step.nodeId == null) break
        nodes = nodes.map((n) =>
          n.id === step.nodeId
            ? { ...n, highlightColor: "var(--color-primary)" }
            : n
        )
        break
      }

      case "reset": {
        nodes = nodes.map((n) => ({
          ...n,
          highlightColor: undefined,
          isNew: false,
          pointerHighlighted: false,
        }))
        break
      }
    }
  }

  return nodes
}

// ── Arrow marker id helpers ──

const MARKER_FWD = "ll-arrow-fwd"
const MARKER_BWD = "ll-arrow-bwd"

// ── Component ──

export function LinkedListViz({
  initialList,
  steps,
  width = 600,
  height = 180,
  doubly = false,
}: LinkedListVizProps) {
  const playback = usePlayback(steps.length)

  const currentNodes = useMemo(
    () =>
      playback.stepIndex < 0
        ? initialList.map((n): InternalNode => ({ id: n.id, value: n.value }))
        : buildListAtStep(initialList, steps, playback.stepIndex),
    [initialList, steps, playback.stepIndex]
  )

  // Compute horizontal offsets — center the chain vertically
  const totalWidth = currentNodes.length * CELL_W + GAP // extra gap for null terminator
  const offsetX = 16
  const offsetY = (height - NODE_H) / 2

  const currentStep =
    playback.stepIndex >= 0 && playback.stepIndex < steps.length
      ? steps[playback.stepIndex]
      : null

  return (
    <div className="flex flex-col gap-3">
      <div
        className="overflow-x-auto rounded-lg border border-border bg-background"
        style={{ maxWidth: width }}
      >
        <svg
          width={Math.max(width, totalWidth + offsetX * 2 + 40)}
          height={height}
        >
          {/* Arrow marker definitions */}
          <defs>
            <marker
              id={MARKER_FWD}
              markerWidth="8"
              markerHeight="6"
              refX="8"
              refY="3"
              orient="auto"
            >
              <polygon
                points="0 0, 8 3, 0 6"
                fill="var(--color-border)"
              />
            </marker>
            <marker
              id={MARKER_BWD}
              markerWidth="8"
              markerHeight="6"
              refX="0"
              refY="3"
              orient="auto"
            >
              <polygon
                points="8 0, 0 3, 8 6"
                fill="var(--color-border)"
              />
            </marker>
            <style>{`
              @keyframes fadeIn {
                from { opacity: 0; }
                to   { opacity: 1; }
              }
            `}</style>
          </defs>

          <g transform={`translate(${offsetX}, ${offsetY})`}>
            {/* HEAD label */}
            <text
              x={0}
              y={-10}
              fontSize={11}
              fontWeight={600}
              fill="var(--color-muted-foreground)"
              style={{ userSelect: "none" }}
            >
              HEAD
            </text>

            {/* Nodes */}
            {currentNodes.map((node, idx) => {
              const x = idx * CELL_W

              return (
                <g
                  key={`node-${node.id}`}
                  style={{
                    transition: "all 400ms ease",
                    opacity: node.isNew ? 0 : 1,
                    animation: node.isNew
                      ? "fadeIn 400ms ease forwards"
                      : undefined,
                  }}
                >
                  <rect
                    x={x}
                    y={0}
                    width={NODE_W}
                    height={NODE_H}
                    rx={NODE_RX}
                    fill="var(--color-card)"
                    stroke={node.highlightColor ?? "var(--color-border)"}
                    strokeWidth={node.highlightColor ? 3 : 1.5}
                    style={{ transition: "all 400ms ease" }}
                  />
                  <text
                    x={x + NODE_W / 2}
                    y={NODE_H / 2}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fontSize={14}
                    fontWeight={500}
                    fill="var(--color-foreground)"
                    style={{
                      pointerEvents: "none",
                      userSelect: "none",
                      transition: "fill 400ms ease",
                    }}
                  >
                    {String(node.value)}
                  </text>
                </g>
              )
            })}

            {/* Arrows between consecutive nodes */}
            {currentNodes.map((node, idx) => {
              if (idx === currentNodes.length - 1) return null
              const x1 = idx * CELL_W + NODE_W
              const x2 = (idx + 1) * CELL_W
              const cy = NODE_H / 2
              const isPointerHighlighted = node.pointerHighlighted

              return (
                <g key={`arrow-${node.id}`}>
                  {/* Forward arrow */}
                  <line
                    x1={x1}
                    y1={cy}
                    x2={x2}
                    y2={cy}
                    stroke={
                      isPointerHighlighted
                        ? "var(--color-primary)"
                        : "var(--color-border)"
                    }
                    strokeWidth={isPointerHighlighted ? 2.5 : 1.5}
                    markerEnd={`url(#${MARKER_FWD})`}
                    style={{ transition: "all 400ms ease" }}
                  />

                  {/* Backward arrow for doubly linked */}
                  {doubly && (
                    <line
                      x1={x2}
                      y1={cy + 8}
                      x2={x1}
                      y2={cy + 8}
                      stroke="var(--color-border)"
                      strokeWidth={1.5}
                      markerEnd={`url(#${MARKER_BWD})`}
                      style={{ transition: "all 400ms ease" }}
                    />
                  )}
                </g>
              )
            })}

            {/* Null terminator after last node */}
            {currentNodes.length > 0 && (
              <g>
                <line
                  x1={(currentNodes.length - 1) * CELL_W + NODE_W}
                  y1={NODE_H / 2}
                  x2={(currentNodes.length - 1) * CELL_W + NODE_W + GAP - 4}
                  y2={NODE_H / 2}
                  stroke="var(--color-border)"
                  strokeWidth={1.5}
                  markerEnd={`url(#${MARKER_FWD})`}
                  style={{ transition: "all 400ms ease" }}
                />
                <text
                  x={currentNodes.length * CELL_W + 2}
                  y={NODE_H / 2}
                  textAnchor="start"
                  dominantBaseline="central"
                  fontSize={12}
                  fontWeight={500}
                  fontStyle="italic"
                  fill="var(--color-muted-foreground)"
                  style={{ userSelect: "none" }}
                >
                  null
                </text>
              </g>
            )}
          </g>
        </svg>
      </div>

      <PlaybackControls
        message={currentStep?.message ?? ""}
        isPlaying={playback.isPlaying}
        atStart={playback.atStart}
        atEnd={playback.atEnd}
        stepIndex={playback.stepIndex}
        totalSteps={steps.length}
        onStepBack={playback.stepBack}
        onStepForward={playback.stepForward}
        onPlay={playback.play}
        onPause={playback.pause}
        onReset={playback.reset}
      />
    </div>
  )
}
